import { createTheme, ThemeOptions } from "@mui/material/styles";

const lightColors = {
  primary: "#274c77",
  secondary: "#f8412a",
  background: "#f5f8ff",
  white: "#ffffff",
  gradient: {
    start: "#ff705e",
    end: "#f8412a",
  },
  text: {
    primary: "#274c77",
    secondary: "rgba(39, 76, 119, 0.7)",
  },
};

const darkColors = {
  primary: "#64b5f6",
  secondary: "#ff9800",
  background: "#121212",
  white: "#1e1e1e",
  gradient: {
    start: "#ff9800",
    end: "#ff6f00",
  },
  text: {
    primary: "#e0e0e0",
    secondary: "rgba(224, 224, 224, 0.7)",
  },
};

const createAppTheme = (isDark: boolean) => {
  const colors = isDark ? darkColors : lightColors;

  const themeOptions: ThemeOptions = {
    palette: {
      mode: isDark ? "dark" : "light",
      primary: {
        main: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      background: {
        default: colors.background,
        paper: colors.white,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
      },
    },
    typography: {
      fontFamily: ["Poppins", "Roboto", "sans-serif"].join(","),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*": {
            // Custom scrollbar for all elements (Webkit browsers)
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(0, 0, 0, 0.3)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: isDark
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(0, 0, 0, 0.5)",
            },
            // Firefox scrollbar styling
            scrollbarWidth: "thin",
            scrollbarColor: isDark
              ? "rgba(255, 255, 255, 0.3) transparent"
              : "rgba(0, 0, 0, 0.3) transparent",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "16px 24px 0px 24px",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "0 24px 20px 24px",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            "&.Mui-disabled": {
              background: "rgba(0, 0, 0, 0.12)",
              color: "rgba(0, 0, 0, 0.26)",
            },
          },
          contained: {
            background: `linear-gradient(316deg, ${colors.gradient.start}, ${colors.gradient.end})`,
            color: colors.white,
            "&:hover": {
              background: `linear-gradient(316deg, ${colors.gradient.start}, ${colors.gradient.end})`,
              opacity: 0.9,
            },
          },
          text: {
            color: colors.text.primary,
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(224, 224, 224, 0.08)"
                : "rgba(39, 76, 119, 0.04)",
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.white,
            color: colors.text.primary,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5",
            color: colors.text.primary,
            fontWeight: 600,
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

export const lightTheme = createAppTheme(false);
export const darkTheme = createAppTheme(true);
export const theme = lightTheme;
